import { $, CustomEventWithDetail, html as runeHtml, html, View } from 'rune-ts';
import klass from './CateListWithRanking.module.scss';
import {
  CategoryLevel1_CODE,
  CategoryLevel2_CODE,
  ICategory,
} from '../../../../../../shared/mps-shared/domain/category/types.ts';
import { font } from '@services/MarppleShop/shared/typography';
import { MpsCategory } from '../../../../../../shared/mps-shared/domain/category/accessor.ts';
import { chunk } from '@fxts/core';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@services/MarppleShop/shared/components/atoms/Icon/foundation_icons.ts';
import { Navigation } from 'swiper/modules';
import Swiper from 'swiper';
import { createBuilderByCateRootCode } from '@services/MarppleShop/features/ProductList/util';
import { CategoryServiceS } from '../../../../../../shared/mps-shared/service/category-share.service.ts';

export interface CateListWithRankingViewData {
  cate_root_code: CategoryLevel1_CODE;
  cate_root: ICategory;
  is_ranking_page: boolean;
  selected_cate_list_code?: string;
  without_sold_out?: boolean;
}

interface CateListWithRankingViewOption {
  klass?: string;
  is_mobile: boolean;
}

export class CateListWithRankingView extends View<CateListWithRankingViewData> {
  state: CateListWithRankingViewOption;
  slides: { img: string | null; name: string | null; selected: boolean; href: string; id?: number }[];
  private static PC_CHUNK = 12;
  private static NAME_LEN = {
    en: 10,
    jp: 8,
    kr: 8,
  };

  private static NAME_MO_LEN = {
    en: 10,
    jp: 8,
    kr: 7,
  };

  private cateRoot: MpsCategory = CategoryServiceS.createMpsCategoryAccessor(this.data.cate_root);

  constructor(data: CateListWithRankingViewData, option: CateListWithRankingViewOption) {
    super(data);

    this.state = {
      klass: option.klass,
      is_mobile: option.is_mobile,
    };

    this.slides = this._makeSlides;
  }

  override template() {
    if (this.state.is_mobile) {
      return html`<div class="${this.state.klass} ${klass.cate_list_with_ranking}">
        <div class="${klass.container} swiper-container">
          <div class="${klass.wrapper} swiper-wrapper">
            ${this.slides.map(({ img, name, selected, href, id }) => {
              return html`
                <div class="swiper-slide ${klass.slide}">
                  <a
                    href="${href}"
                    class="${klass.cate_list} ${selected ? klass.selected : ''}"
                    data-cate_list_id="${id || ''}"
                  >
                    <div class="${klass.img_wrapper}">
                      ${img ? html`<img src="${img || ''}" alt="${name}" />` : ''}
                    </div>
                    <div class="${klass.name} ${this._makeFont(selected)}">
                      <span>${this._makeCateListName(name)}</span>
                    </div>
                  </a>
                </div>
              `;
            })}
          </div>
        </div>
      </div>`;
    }
    const slides = [...chunk(CateListWithRankingView.PC_CHUNK, this.slides)];
    return html`<div class="${this.state.klass} ${klass.cate_list_with_ranking}">
      <div class="${klass.container} swiper-container">
        <div class="${klass.wrapper} swiper-wrapper">
          ${slides.map((items) => {
            return html`
              <div class="swiper-slide ${klass.slide}">
                ${items.map(({ img, name, selected, href, id }) => {
                  return html`
                    <a
                      href="${href}"
                      class="${klass.cate_list} ${selected ? klass.selected : ''}"
                      data-cate_list_id="${id || ''}"
                    >
                      <div class="${klass.img_wrapper}">
                        ${img ? html`<img src="${img || ''}" alt="${name}" />` : ''}
                      </div>
                      <div class="${klass.name} ${this._makeFont(selected)}">
                        ${this._makeCateListName(name)}
                      </div>
                    </a>
                  `;
                })}
              </div>
            `;
          })}
        </div>
      </div>
      ${slides.length > 1
        ? html`
            <button class="${klass.swiper_btn} ${klass.swiper_btn_prev} ${klass.swiper_btn_disabled}">
              ${ChevronLeftIcon()}
            </button>
            <button class="${klass.swiper_btn} ${klass.swiper_btn_next}">${ChevronRightIcon()}</button>
          `
        : ''}
    </div> `;
  }

  private _makeCateListName(name: string | null) {
    if (!name) {
      return '';
    }
    const name_len = this.state.is_mobile
      ? CateListWithRankingView.NAME_MO_LEN[ET.lang]
      : CateListWithRankingView.NAME_LEN[ET.lang];
    const is_over_len = name?.length > name_len;
    const replacer = (name?.split('/')[0]?.length || 0) < 9 ? '/<br />' : '<br />/';
    return is_over_len ? runeHtml.preventEscape(name?.replace('/', replacer)) : name;
  }

  private get _makeSlides() {
    const { selected_cate_list_code } = this.data;
    const { is_mobile } = this.state;
    const ranking = {
      img: '//s3.marpple.co/files/u_29089/2025/7/original/13fa541d5b2feac840bfaeeecbf3b8b1aec583731.png',
      selected: !selected_cate_list_code,
      name: is_mobile ? ET('mps2::ranking::cate_roots::ranking') : this.cateRoot.getRankName(),
      href: this._makeRankingHref(),
    };
    return [
      // ranking,
      ...this.cateRoot
        .getChildren()
        .filter((cate) => ET.lang === 'kr' || cate.getCode() !== CategoryLevel2_CODE.DIGITAL_GOODS)
        .map((cate) => {
          const img = cate.getImageUrl();
          const name = cate.getName() || '';
          const selected = selected_cate_list_code == cate.getCode();
          const href = this.makeProductHref(cate.getId());
          return {
            id: cate.getId(),
            img,
            name,
            selected,
            href,
          };
        }),
    ];
  }

  private _makeRankingHref() {
    const urlBuilder = createBuilderByCateRootCode(this.data.cate_root_code);

    return urlBuilder.build();
  }

  private makeProductHref(cate_list_id: number) {
    const urlBuilder = createBuilderByCateRootCode(this.data.cate_root_code);

    return urlBuilder
      .setPage('PRODUCTS')
      .setFilters({ cate_list_id, without_sold_out: this.data.without_sold_out })
      .build();
  }

  private selectedCateListIndex() {
    return this.slides.findIndex((slide) => slide.selected);
  }

  private setSelectedCateListCode(cate_list_id: number) {
    const cate_list = this.cateRoot.getChildren().find((cate_list) => {
      return cate_list.getId() == cate_list_id;
    });
    if (cate_list) {
      this.data.selected_cate_list_code = cate_list.getCode();
    }
  }

  private _makeFont(is_selected: boolean) {
    if (is_selected) {
      if (this.state.is_mobile) {
        return font('12_bold2');
      } else {
        return font('14_bold2');
      }
    } else {
      if (this.state.is_mobile) {
        return font('12_medium2');
      } else {
        return font('14_medium2');
      }
    }
  }

  private _getSelctedEl$() {
    return $(this.element()).find(`.${klass.selected}[data-cate_list_id]`);
  }

  public getSelectedCateList() {
    return this.cateRoot.getChildren().find((cate_list) => {
      return cate_list.getCode() == this.data.selected_cate_list_code;
    });
  }

  protected override onRender() {
    if (!this.state.is_mobile) {
      new Swiper(`.${klass.container}`, {
        modules: [Navigation],
        slidesPerView: 'auto',
        allowTouchMove: true,
        navigation: {
          nextEl: `.${klass.swiper_btn_next}`,
          prevEl: `.${klass.swiper_btn_prev}`,
          disabledClass: `${klass.swiper_btn_disabled}`,
        },
        on: {
          init: (swiper) => {
            swiper.slideTo(Math.floor(this.selectedCateListIndex() / CateListWithRankingView.PC_CHUNK), 0);
          },
        },
      });
    } else {
      const selected$ = this._getSelctedEl$();
      if (selected$) {
        const container$ = $(this.element()).find(`.${klass.container}`);
        const slide$ = selected$.closest(`.${klass.slide}`);
        if (container$ && slide$) {
          container$.element().scrollLeft = slide$.element().offsetLeft - 16;
        }
      }
    }

    if (!this.data.is_ranking_page) {
      this.delegate('click', `[data-cate_list_id]:not(.${klass.selected})`, (e) => {
        const cate_list$ = e.currentTarget as HTMLElement;
        const cate_list_id = Number(cate_list$.dataset.cate_list_id);
        if (cate_list_id) {
          const selected_item$ = this._getSelctedEl$();
          if (selected_item$) {
            selected_item$
              .removeClass(klass.selected)
              .find(`.${klass.name}`)
              ?.removeClass(this._makeFont(true))
              ?.addClass(this._makeFont(false));
          }
          $(cate_list$).addClass(klass.selected).find(`.${klass.name}`)?.addClass(this._makeFont(true));

          this.setSelectedCateListCode(cate_list_id);

          this.dispatchEvent(CateListWithRankingEvent, {
            bubbles: true,
            detail: {
              cate_list_id,
              originalEvent: e.originalEvent,
              view: this,
            },
          });
        }
      });
    }
  }
}

export class CateListWithRankingEvent extends CustomEventWithDetail<{
  cate_list_id: number;
  originalEvent: MouseEvent;
  view: CateListWithRankingView;
}> {}
